import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { Instructions } from './components/Instructions';
import { RegistrationForm } from './components/RegistrationForm';
import { supabase } from './supabaseClient';
import { CollegeRegistration, EventSettings, ViewState } from './types';

const AdminDashboard = lazy(() => import('./components/Dashboard').then(module => ({ default: module.AdminDashboard })));
const AdminLogin = lazy(() => import('./components/AdminLogin').then(module => ({ default: module.AdminLogin })));
const PassView = lazy(() => import('./components/PassView').then(module => ({ default: module.PassView })));

const DEFAULT_SETTINGS: EventSettings = {
  eventName: "SANGHARSH '26",
  tagline: "ANNUAL NCC FEST OF ARSD COLLEGE",
  date: "18 February 2026",
  location: "ARSD College Premises",
  description: "Struggle. Strength. Success. The premier inter-college NCC competition gathering the finest cadets across the nation.",
  primaryColor: "#d4af37" 
};

const App: React.FC = () => {
  const [view, setView] = useState<ViewState>('HOME');
  const [registrations, setRegistrations] = useState<CollegeRegistration[]>([]);
  const [settings, setSettings] = useState<EventSettings>(DEFAULT_SETTINGS);
  const [currentReg, setCurrentReg] = useState<CollegeRegistration | null>(null);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      const { data: regsData, error: regsError } = await supabase.from('registrations').select('*');
      if (regsData) setRegistrations(regsData);
      if (regsError) console.error("Error fetching registrations:", regsError);

      const { data: settingsData, error: settingsError } = await supabase.from('settings').select('*').single();
      if (settingsData) setSettings(settingsData);
      if (settingsError) console.error("Error fetching settings:", settingsError);
    };
    fetchInitialData();
  }, []);

  const handleRegister = async (data: Omit<CollegeRegistration, 'id' | 'timestamp' | 'passId' | 'hasCheckedIn' | 'hasLunchUsed' | 'isFinalized' | 'unitOf' | 'sdCount' | 'swCount'>) => {
    const passId = `CAD-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
    const newRegData = {
      ...data,
      passId,
    };

    const { data: newReg, error } = await supabase.from('registrations').insert(newRegData).select().single();
    
    if (newReg) {
      setRegistrations(prev => [...prev, newReg as CollegeRegistration]);
      setCurrentReg(newReg as CollegeRegistration);
      setView('MY_PASSES');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      console.error('Error during registration:', error);
      alert('An error occurred during registration. Please check the console for details and try again.');
    }
  };

  const handleRetrievePass = async (searchQuery: string) => {
    const { data, error } = await supabase
      .from('registrations')
      .select('*')
      .or(`passId.eq.${searchQuery.toUpperCase()},email.eq.${searchQuery.toLowerCase()}`)
      .single();

    if (data) {
      setCurrentReg(data);
      setView('MY_PASSES');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      alert("No registration found with that ID or Email. Please check and try again.");
       if (error) console.error('Error retrieving pass:', error);
    }
  };

  const toggleAction = async (id: string, field: 'hasCheckedIn' | 'hasLunchUsed') => {
    const currentReg = registrations.find(r => r.id === id);
    if (!currentReg) return;

    const { data, error } = await supabase
      .from('registrations')
      .update({ [field]: !currentReg[field] })
      .eq('id', id)
      .select()
      .single();

    if (data) {
      setRegistrations(prev => prev.map(r => r.id === id ? (data as CollegeRegistration) : r));
    } else {
      console.error('Error toggling action:', error);
    }
  };

  const deleteRegistration = async (id: string) => {
    if (confirm("Are you sure you want to delete this college registration?")) {
      const { error } = await supabase.from('registrations').delete().eq('id', id);
      if (!error) {
        setRegistrations(prev => prev.filter(r => r.id !== id));
      } else {
        console.error('Error deleting registration:', error);
      }
    }
  };

  const handleUpdateSettings = async (newSettings: EventSettings) => {
    // Assuming settings have a unique, known ID, e.g., created at setup
    // For this example, let's assume there's only one row and we don't know its ID.
    // A better approach would be to fetch and store the settings ID in the state.
    const { data, error } = await supabase.from('settings').update(newSettings).eq('id', settings.id).select().single();
    if (data) {
      setSettings(data);
      alert('Settings updated!');
    } else {
      console.error('Error updating settings:', error);
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar currentView={view} setView={setView} eventName={settings.eventName} />
      
      <main className="flex-grow pt-20">
        <Suspense fallback={<div className="text-center p-10">Loading Interface...</div>}>
          {view === 'HOME' && (
            <Hero 
              settings={settings} 
              onRegisterClick={() => setView('REGISTER')} 
              onRetrievePass={handleRetrievePass}
            />
          )}
          {view === 'INSTRUCTIONS' && <Instructions onAccept={() => setView('REGISTER')} />}
          {view === 'REGISTER' && <RegistrationForm onRegister={handleRegister} onBack={() => setView('HOME')} />}
          {view === 'ADMIN' && (
            isAdminLoggedIn ? (
              <div className="max-w-7xl mx-auto px-4 py-8">
                <AdminDashboard 
                  registrations={registrations} 
                  settings={settings}
                  onUpdateSettings={handleUpdateSettings}
                  onToggleAction={toggleAction}
                  onDelete={deleteRegistration}
                />
              </div>
            ) : (
              <AdminLogin 
                onLogin={() => setIsAdminLoggedIn(true)} 
                onCancel={() => setView('HOME')} 
              />
            )
          )}
          {view === 'MY_PASSES' && currentReg && (
            <PassView 
              registration={currentReg} 
              settings={settings} 
              onBack={() => setView('HOME')}
            />
          )}
        </Suspense>
      </main>

      <footer className="py-12 border-t border-gold/10 text-center glass mt-auto">
        <div className="max-w-7xl mx-auto px-4 flex flex-col items-center">
          <p className="font-stencil tracking-[0.3em] text-gold opacity-50 mb-2">{settings.eventName}</p>
          <p className="text-gray-500 text-[10px] uppercase tracking-widest">&copy; 2026 ARSD NCC Unit. All Rights Reserved.</p>
          
          <div className="mt-8 pt-8 border-t border-white/5 w-full max-w-xs">
            <button 
              onClick={() => setView('ADMIN')}
              className="text-[9px] font-black text-gray-700 hover:text-gold/40 transition-colors uppercase tracking-[0.5em]"
            >
              • Admin Access Portal •
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;